import { ref, computed, type Ref } from 'vue';

const MAX_CONSOLE_ENTRIES = 200;
const MAX_NETWORK_ENTRIES = 100;

export interface ConsoleLogEntry {
  id: number;
  time: string;
  level: 'log' | 'warn' | 'error' | 'info' | 'debug';
  message: string;
}

export interface NetworkLogEntry {
  id: number;
  time: string;
  domain: string;
  method: string;
  name: string;
  status: number | string;
  timing: string;
  size: string;
}

let consoleId = 0;
let networkId = 0;

function formatTime(): string {
  const d = new Date();
  return d.toLocaleTimeString('en-US', {
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
}

/**
 * Show clean DNS-style hostname: strip leading hash-like subdomains (e.g. 3b09...e8cd389.appgw.conviva.com → appgw.conviva.com).
 */
function getDomain(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const host = u.hostname.replace(/^www\./, '');
    const labels = host.split('.');
    if (labels.length >= 2) {
      const first = labels[0];
      if (first.length >= 24 && /^[a-f0-9]+$/i.test(first)) {
        return labels.slice(1).join('.');
      }
    }
    return host;
  } catch {
    return 'OTHER';
  }
}

/** Extract only the asset filename with extension (e.g. index.m3u8 from a full URL path). */
function trimRequestName(url: string, method: string): string {
  try {
    const u = new URL(url, window.location.origin);
    const pathname = (u.pathname || '').trim();
    const segments = pathname.split('/').filter(Boolean);
    const last = segments[segments.length - 1];
    if (last) return last;
    if (pathname === '/' || !pathname) return method + ' [root]';
    return method + ' [unknown]';
  } catch {
    return method + ' [invalid]';
  }
}

function formatSize(bytes: number): string {
  if (bytes === 0) return '0B';
  if (bytes < 1024) return bytes + 'B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + 'KB';
  return (bytes / (1024 * 1024)).toFixed(1) + 'MB';
}

let originalConsole: {
  log: typeof console.log;
  warn: typeof console.warn;
  error: typeof console.error;
  info: typeof console.info;
  debug: typeof console.debug;
} | null = null;
let originalFetch: typeof fetch | null = null;
let originalXHROpen: typeof XMLHttpRequest.prototype.open | null = null;
let originalXHRSend: typeof XMLHttpRequest.prototype.send | null = null;
let intercepting = false;

function installConsoleInterceptor(push: (entry: ConsoleLogEntry) => void) {
  if (intercepting || originalConsole) return;
  originalConsole = {
    log: console.log.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
    info: console.info.bind(console),
    debug: console.debug.bind(console),
  };
  const levels: Array<'log' | 'warn' | 'error' | 'info' | 'debug'> = ['log', 'warn', 'error', 'info', 'debug'];
  levels.forEach(level => {
    (console as any)[level] = (...args: any[]) => {
      const message = args.map(a => (typeof a === 'object' ? JSON.stringify(a) : String(a))).join(' ');
      push({
        id: ++consoleId,
        time: formatTime(),
        level,
        message: message.slice(0, 500),
      });
      originalConsole![level](...args);
    };
  });
  intercepting = true;
}

function uninstallConsoleInterceptor() {
  if (!originalConsole) return;
  (['log', 'warn', 'error', 'info', 'debug'] as const).forEach(level => {
    (console as any)[level] = originalConsole![level];
  });
  originalConsole = null;
  intercepting = false;
}

function installNetworkInterceptor(push: (entry: NetworkLogEntry) => void) {
  if (originalFetch !== null) return;

  originalFetch = window.fetch;
  window.fetch = function (input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    const method = (
      init?.method || (typeof input !== 'string' && input instanceof Request ? input.method : 'GET')
    ).toUpperCase();
    const start = performance.now();
    return originalFetch!(input, init).then(
      response => {
        const timing = `${Math.round(performance.now() - start)}ms`;
        response
          .clone()
          .text()
          .then(
            body => {
              push({
                id: ++networkId,
                time: formatTime(),
                domain: getDomain(url),
                method,
                name: trimRequestName(url, method),
                status: response.status,
                timing,
                size: formatSize(new Blob([body]).size),
              });
            },
            () => {
              push({
                id: ++networkId,
                time: formatTime(),
                domain: getDomain(url),
                method,
                name: trimRequestName(url, method),
                status: response.status,
                timing,
                size: '—',
              });
            },
          );
        return response;
      },
      reason => {
        push({
          id: ++networkId,
          time: formatTime(),
          domain: getDomain(typeof url === 'string' ? url : (url as any).href),
          method: method || 'GET',
          name: trimRequestName(typeof url === 'string' ? url : (url as any).href, method || 'GET'),
          status: 'ERR',
          timing: `${Math.round(performance.now() - start)}ms`,
          size: '0B',
        });
        throw reason;
      },
    );
  };

  originalXHROpen = XMLHttpRequest.prototype.open;
  originalXHRSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function (method: string, url: string, ...args: unknown[]) {
    (this as any).__debugUrl = url;
    (this as any).__debugMethod = (method || 'GET').toUpperCase();
    (this as any).__debugStart = performance.now();
    return (originalXHROpen as (...a: unknown[]) => unknown).apply(this, [method, url, ...args]);
  };
  XMLHttpRequest.prototype.send = function (...args: unknown[]) {
    const url = (this as any).__debugUrl || '';
    const method = (this as any).__debugMethod || 'GET';
    const start = (this as any).__debugStart || performance.now();
    const onDone = function (this: XMLHttpRequest) {
      const timing = `${Math.round(performance.now() - start)}ms`;
      const status = this.status;
      let size = '0B';
      try {
        if (this.responseType === '' || this.responseType === 'text')
          size = formatSize(new Blob([this.responseText]).size);
        else if (this.response) size = formatSize(this.response.byteLength || 0);
      } catch {
        size = '—';
      }
      push({
        id: ++networkId,
        time: formatTime(),
        domain: getDomain(url),
        method,
        name: trimRequestName(url, method),
        status,
        timing,
        size,
      });
    };
    this.addEventListener('load', onDone);
    this.addEventListener('error', onDone);
    this.addEventListener('abort', onDone);
    return (originalXHRSend as (...a: unknown[]) => unknown).apply(this, args);
  };
}

function uninstallNetworkInterceptor() {
  if (originalFetch) {
    window.fetch = originalFetch;
    originalFetch = null;
  }
  if (originalXHROpen && originalXHRSend) {
    XMLHttpRequest.prototype.open = originalXHROpen;
    XMLHttpRequest.prototype.send = originalXHRSend;
    originalXHROpen = null;
    originalXHRSend = null;
  }
}

export function useOnScreenDebugger(isDeveloper: Ref<boolean>) {
  const panelExpanded = ref(false);
  const activeTab = ref<'console' | 'network' | 'video' | 'general'>('console');
  const paused = ref(false);

  const consoleLogs = ref<ConsoleLogEntry[]>([]);
  const networkLogs = ref<NetworkLogEntry[]>([]);

  const pushConsole = (entry: ConsoleLogEntry) => {
    if (paused.value) return;
    consoleLogs.value = [...consoleLogs.value.slice(-(MAX_CONSOLE_ENTRIES - 1)), entry];
  };
  const pushNetwork = (entry: NetworkLogEntry) => {
    if (paused.value) return;
    networkLogs.value = [...networkLogs.value.slice(-(MAX_NETWORK_ENTRIES - 1)), entry];
  };

  function startInterception() {
    if (!isDeveloper.value) return;
    installConsoleInterceptor(pushConsole);
    installNetworkInterceptor(pushNetwork);
  }

  function stopInterception() {
    uninstallConsoleInterceptor();
    uninstallNetworkInterceptor();
  }

  function togglePanel() {
    if (!isDeveloper.value) return;
    if (panelExpanded.value) {
      panelExpanded.value = false;
      stopInterception();
    } else {
      panelExpanded.value = true;
      startInterception();
    }
  }

  function closeDebugger() {
    stopInterception();
  }

  function showConsoleTab() {
    if (!isDeveloper.value) return;
    panelExpanded.value = true;
    startInterception();
    activeTab.value = 'console';
  }

  function showNetworkTab() {
    if (!isDeveloper.value) return;
    panelExpanded.value = true;
    startInterception();
    activeTab.value = 'network';
  }

  function showVideoTab() {
    if (!isDeveloper.value) return;
    panelExpanded.value = true;
    startInterception();
    activeTab.value = 'video';
  }

  function showGeneralTab() {
    if (!isDeveloper.value) return;
    panelExpanded.value = true;
    startInterception();
    activeTab.value = 'general';
  }

  function clearAll() {
    consoleLogs.value = [];
    networkLogs.value = [];
  }

  function togglePause() {
    if (!isDeveloper.value) return;
    paused.value = !paused.value;
  }

  const consoleCount = computed(() => consoleLogs.value.length);
  const networkCount = computed(() => networkLogs.value.length);

  return {
    panelExpanded,
    activeTab,
    paused,
    consoleLogs,
    networkLogs,
    consoleCount,
    networkCount,
    togglePanel,
    togglePause,
    closeDebugger,
    showConsoleTab,
    showNetworkTab,
    showVideoTab,
    showGeneralTab,
    clearAll,
    startInterception,
    stopInterception,
  };
}
